import { CommonModule } from '@angular/common';
import {
    afterNextRender,
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    Injector,
    input,
    output,
    signal,
    ViewChild,
} from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { DxFileUploaderComponent, DxFileUploaderModule } from 'devextreme-angular/ui/file-uploader';
import { DxTooltipModule } from 'devextreme-angular/ui/tooltip';
import type { UploadedEvent, ValueChangedEvent } from 'devextreme/ui/file_uploader';
import { ApiService } from 'src/app/core/services/api.service';
import { NotifyService } from 'src/app/core/services/notify.service';

@Component({
    selector: 'app-form-photo-uploader',
    templateUrl: './form-photo-uploader.component.html',
    styleUrls: ['./form-photo-uploader.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, DxFileUploaderModule, TranslocoPipe, DxTooltipModule],
})
export class FormPhotoUploaderComponent implements AfterViewInit {
    private readonly injector = inject(Injector);
    private readonly translocoService = inject(TranslocoService);
    private readonly apiService = inject(ApiService);
    private readonly notifyService = inject(NotifyService);

    @ViewChild('fileUploader', { static: false }) fileUploader?: DxFileUploaderComponent;
    @ViewChild('uploader', { static: false }) uploaderElement?: ElementRef<HTMLDivElement>;

    placeholder = input<string>('dragAndDropFilesHereOrSelectFiles');
    height = input<number>(124);
    existingImageUrl = input<string | null>(null);
    disabled = input<boolean>(false);
    removable = input<boolean>(true);
    validFormats = signal<string | null>('.jpg,.jpeg,.png,.gif,.webp');
    readonly allowedExtensions = computed(() => this.validFormats()?.split(','));

    readonly uploadedFile = output<File | null>();
    /** Temp file id (`fileGuid`) returned by the upload API after upload succeeds. */
    readonly tempFileId = output<string | null>();
    readonly uploadingChange = output<boolean>();

    readonly isDropZoneActive = signal(false);
    readonly selectedFile = signal<File | null>(null);
    readonly isUploading = signal(false);
    previewUrl = signal<string | null>(null);
    readonly uploadUrl = this.apiService.pathUrl.tempFileUpload();

    private bindDropZoneRetryCount = 0;

    constructor() {
        effect(() => {
            const existingUrl = this.existingImageUrl();
            const selectedFile = this.selectedFile();
            if (selectedFile) {
                return;
            }
            if (existingUrl) {
                this.previewUrl.set(existingUrl);
            } else {
                this.previewUrl.set(null);
            }
        });

        effect(() => {
            this.disabled();
            afterNextRender(() => this.applyDropZoneToFileUploader(), { injector: this.injector });
        });
    }

    /** Binds after view init; DevExtreme accepts HTMLElement (not only #id selectors). */
    ngAfterViewInit(): void {
        afterNextRender(() => this.applyDropZoneToFileUploader(), { injector: this.injector });
    }

    private applyDropZoneToFileUploader(): void {
        const el = this.uploaderElement?.nativeElement;
        const widget = this.fileUploader?.instance;
        if (!el) {
            return;
        }
        if (!widget) {
            if (this.bindDropZoneRetryCount < 15) {
                this.bindDropZoneRetryCount++;
                queueMicrotask(() => this.applyDropZoneToFileUploader());
            }
            return;
        }
        this.bindDropZoneRetryCount = 0;
        if (this.disabled()) {
            widget.option('dialogTrigger', '');
            widget.option('dropZone', '');
        } else {
            widget.option('dialogTrigger', el);
            widget.option('dropZone', el);
        }
    }

    private isOurDropZone(dropZoneElement: unknown): boolean {
        const el = this.uploaderElement?.nativeElement;
        if (!el || dropZoneElement == null) {
            return false;
        }
        if (dropZoneElement === el) {
            return true;
        }
        const maybeJquery = dropZoneElement as { readonly 0?: HTMLElement };
        return maybeJquery[0] === el;
    }

    onDropZoneEnter(e: { dropZoneElement: unknown }) {
        if (this.disabled()) return;
        if (this.isOurDropZone(e.dropZoneElement)) {
            this.isDropZoneActive.set(true);
        }
    }

    onDropZoneLeave(e: { dropZoneElement: unknown }) {
        if (this.disabled()) return;
        if (this.isOurDropZone(e.dropZoneElement)) {
            this.isDropZoneActive.set(false);
        }
    }

    onValueChanged(e: ValueChangedEvent): void {
        if (this.disabled()) return;

        const file: File = e.value?.[0];
        if (!file) return;
        const re = /(?:\.([^.]+))?$/;

        if (!this.allowedExtensions().includes('.' + re.exec(file.name)[1].toLowerCase())) {
            this.notifyService.notifyErrorText(
                this.translocoService.translate('wrong_file_extension', { types: this.validFormats().replace(/,/gi, ', ') })
            );
            return;
        }

        this.handleFileSelection(file);
    }

    onUploadStarted(): void {
        this.isUploading.set(true);
        this.uploadingChange.emit(true);
    }

    onUploaded(e: UploadedEvent): void {
        this.isUploading.set(false);
        this.uploadingChange.emit(false);
        const guid = this.extractFileGuidFromUploadEvent(e);

        if (guid) {
            this.tempFileId.emit(guid);
        } else {
            this.tempFileId.emit(null);
            this.notifyService.notifyErrorText(this.translocoService.translate('avatarUploadFail') || 'Upload failed: missing file reference.');
        }
    }

    onUploadError(): void {
        this.isUploading.set(false);
        this.uploadingChange.emit(false);
        this.tempFileId.emit(null);
        this.notifyService.notifyErrorText(this.translocoService.translate('avatarUploadFail') || 'Upload failed.');
        this.selectedFile.set(null);
        this.previewUrl.set(null);
        this.fileUploader?.instance?.reset();
    }

    private handleFileSelection(file: File): void {
        if (this.disabled()) return;

        if (!file.type.startsWith('image/')) {
            this.notifyService.notifyErrorText(this.translocoService.translate('validation_invalidImage') || 'Please select an image file.');
            return;
        }

        const maxSize = 5 * 1024 * 1024;
        if (file.size > maxSize) {
            this.notifyService.notifyErrorText(this.translocoService.translate('validation_fileTooLarge') || 'File size exceeds limit.');
            return;
        }

        this.selectedFile.set(file);
        this.createPreview(file);
        this.uploadedFile.emit(file);
    }

    private createPreview(file: File): void {
        const reader = new FileReader();
        reader.onload = (ev: ProgressEvent<FileReader>) => {
            const result = ev.target?.result;
            if (typeof result === 'string') {
                this.previewUrl.set(result);
            }
        };
        reader.readAsDataURL(file);
    }

    resetState(): void {
        this.isUploading.set(false);
        this.uploadingChange.emit(false);
        this.selectedFile.set(null);
        this.previewUrl.set(null);
        this.fileUploader?.instance?.reset();
    }

    async removeFile(): Promise<void> {
        if (this.disabled()) return;

        this.uploadingChange.emit(false);

        this.selectedFile.set(null);
        this.previewUrl.set(null);
        this.uploadedFile.emit(null);
        this.tempFileId.emit(null);
    }

    updateFile(): void {
        if (this.disabled()) return;
        const uploaderEl = this.uploaderElement?.nativeElement;
        if (uploaderEl) {
            const wasHidden = uploaderEl.style.display === 'none';
            if (wasHidden) {
                uploaderEl.style.display = 'flex';
                uploaderEl.style.visibility = 'visible';
                uploaderEl.style.position = 'fixed';
                uploaderEl.style.left = '-9999px';
                uploaderEl.style.top = '-9999px';
            }
            uploaderEl.click();
            if (wasHidden) {
                setTimeout(() => {
                    uploaderEl.style.display = 'none';
                    uploaderEl.style.visibility = 'hidden';
                    uploaderEl.style.position = '';
                    uploaderEl.style.left = '';
                    uploaderEl.style.top = '';
                }, 100);
            }
        }
    }

    private extractFileGuidFromUploadEvent(e: UploadedEvent): string | null {
        const response: {
            readonly fileGuid: string;
            readonly controlName: string;
            readonly fileName: string;
        } | null = e.request.response.body;

        if (!response) return null;

        return response.fileGuid;
    }
}
